import { EAlertFormData } from '../schemas/eAlert.schema';
import { createCloudRunAxios } from './axios';

const remiApi = createCloudRunAxios('remi-api');

/**
 * Extract the backend error text from a FastAPI `HTTPException(detail=...)`
 * response. Axios surfaces it as `error.response.data.detail`.
 * - Direct Remi API errors (403 admin_guard, 422 missing org_id, …) →
 *   `detail` is a string.
 * - Errors proxied from the upstream eAlertSystem (Flask) →
 *   `detail` is the upstream JSON body. The real message currently lives
 *   at `detail.error` (e.g. {"success":false,"error":"Contact does not
 *   have an email address","email_missing":true}); legacy/proxied paths
 *   may surface it at `detail.detail` instead.
 */
export const getEAlertErrorMessage = (error: unknown, fallback: string): string => {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (detail && typeof detail === 'object') {
    const body = detail as { detail?: unknown; error?: unknown; email_missing?: unknown };
    // Friendlier copy for the common "contact has no email" 400.
    if (body.email_missing === true) {
      return 'This contact does not have an email address. Add one before setting up an eAlert.';
    }
    const candidates: unknown[] = [body.error, body.detail];
    for (const c of candidates) {
      if (typeof c === 'string' && c.trim()) return c;
    }
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
};
// Legacy alias kept for existing callsites
const getErrorMessage = getEAlertErrorMessage;

/**
 * Register a new eAlert schedule
 */
export const registerEAlert = async (data: EAlertFormData): Promise<unknown> => {
  try {
    const response = await remiApi.post('/api/v1/ealert/register', data);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to register eAlert'));
  }
};

/**
 * Unsubscribe a contact from eAlerts
 */
export const unsubscribeEAlert = async (contactId: string, organizationId: string): Promise<unknown> => {
  try {
    const response = await remiApi.post('/api/v1/ealert/unsubscribe', { contact_id: contactId, organization_id: organizationId });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to unsubscribe from eAlert'));
  }
};

/**
 * Test an eAlert configuration without actually registering it
 */
export const testEAlert = async (data: EAlertFormData): Promise<unknown> => {
  const testData = {
    ...data,
    skip_callback: true,
    test_mode: true,
  };

  try {
    const response = await remiApi.post('/api/v1/ealert/test', testData);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to test eAlert'));
  }
};

/**
 * Update eAlert filters for a contact
 */
export const updateEAlertFilters = async (data: EAlertFormData): Promise<unknown> => {
  try {
    const response = await remiApi.post('/api/v1/ealert/update', data);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to update eAlert filters'));
  }
};
